import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Database, Search } from 'lucide-react';

const SearchResult = ({ searchResult }) => {
  if (!searchResult) return null;
  
  console.log('🎯 DEBUG SearchResult - searchResult recebido:', searchResult);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card"
    >
      <div className="p-6">
        <h3 className="font-medium text-gray-900 mb-4">Resultado da Busca</h3>
        
        {/* Status */}
        <div className="mb-4">
          {searchResult.found ? (
            <div className="flex items-center space-x-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Encontrado</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2 text-red-600">
              <XCircle className="w-5 h-5" />
              <span className="font-medium">Não encontrado</span>
            </div>
          )}
        </div>

        {/* Dados encontrados */}
        {searchResult.found && searchResult.data && (
          <div className="space-y-3">
            <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
              <div className="font-medium mb-2">Dados encontrados:</div>
              <div className="space-y-1">
                {/* ID da cobrança - compatível com ambos formatos */}
                {(searchResult.data.cobrancaID || searchResult.data.cobranca_id) && (
                  <div>ID: {searchResult.data.cobrancaID || searchResult.data.cobranca_id}</div>
                )}
                
                {/* Nome do pagador - compatível com ambos formatos */}
                {(searchResult.data.nomePagador || searchResult.data.nome_pagador) && (
                  <div>Pagador: {searchResult.data.nomePagador || searchResult.data.nome_pagador}</div>
                )}
                
                {/* Valor - compatível com ambos formatos */}
                {(searchResult.data.valorString || searchResult.data.valor_string || searchResult.data.valor) && (
                  <div>Valor: {
                    searchResult.data.valorString || 
                    searchResult.data.valor_string || 
                    `R$ ${parseFloat(searchResult.data.valor || 0).toFixed(2)}`
                  }</div>
                )}
                
                {/* Status - compatível com ambos formatos */}
                {(searchResult.data.statusCobrancaTexto || searchResult.data.status_cobranca_texto) && (
                  <div>Status: {searchResult.data.statusCobrancaTexto || searchResult.data.status_cobranca_texto}</div>
                )}
                
                {/* Vencimento - compatível com ambos formatos */}
                {(searchResult.data.vencimentoString || searchResult.data.vencimento_string || searchResult.data.vencimento) && (
                  <div>Vencimento: {
                    searchResult.data.vencimentoString || 
                    searchResult.data.vencimento_string ||
                    (searchResult.data.vencimento ? new Date(searchResult.data.vencimento).toLocaleDateString('pt-BR') : '')
                  }</div>
                )}
                
                {/* Gerado por - compatível com ambos formatos */}
                {(searchResult.data.geradoPor || searchResult.data.gerado_por) && (
                  <div>Gerado por: {searchResult.data.geradoPor || searchResult.data.gerado_por}</div>
                )}
                
                {/* Código da parcela */}
                {(searchResult.foundParcela || searchResult.data.codigoFalanteRadar || searchResult.data.codigo_falante_radar) && (
                  <div className="text-green-600 font-medium">
                    ✅ Parcela: {
                      searchResult.foundParcela || 
                      searchResult.data.codigoFalanteRadar || 
                      searchResult.data.codigo_falante_radar
                    }
                    {searchResult.endpoint && (
                      <span className="text-blue-600 ml-1">({searchResult.endpoint})</span>
                    )}
                  </div>
                )}

                {/* Documento do pagador (nova funcionalidade) */}
                {(searchResult.data.documentoPagador || searchResult.data.documento_pagador) && (
                  <div>📄 Documento: {searchResult.data.documentoPagador || searchResult.data.documento_pagador}</div>
                )}
              </div>
            </div>
            
            {/* Resumo de Vendas (versão resumida) */}
            {searchResult.data.resumoDeVendas && (
              <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded">
                <div className="font-medium mb-2">📋 Resumo de Vendas:</div>
                <div className="text-xs text-blue-700 line-clamp-3">
                  {searchResult.data.resumoDeVendas.substring(0, 150)}
                  {searchResult.data.resumoDeVendas.length > 150 && '...'}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Informações da Busca Inteligente */}
        {searchResult.searchMethod && (
          <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded mb-3">
            <div className="font-medium mb-2 flex items-center">
              <Search className="w-4 h-4 mr-2" />
              Busca Inteligente
            </div>
            <div className="space-y-1">
              <div>Método: <span className="font-medium">{searchResult.searchMethod}</span></div>
              
              {/* Fonte da busca */}
              <div>
                Fonte: <span className="font-medium">
                  {searchResult.searchSource === 'local_database' && '🗄️ Banco Local'}
                  {searchResult.searchSource === 'api_fallback' && '🌐 API Externa'}
                  {searchResult.searchSource === 'api_and_saved_to_local' && '🌐➡️🗄️ API → Banco'}
                  {searchResult.searchSource && !['local_database', 'api_fallback', 'api_and_saved_to_local'].includes(searchResult.searchSource) && 
                    searchResult.searchSource}
                  {!searchResult.searchSource && '❓ Não informada'}
                </span>
              </div>
              
              {/* Tipo de busca (nova funcionalidade) */}
              {searchResult.searchType && (
                <div>
                  Tipo: <span className="font-medium">
                    {searchResult.searchType === 'pc_code' && '📋 Código PC'}
                    {searchResult.searchType === 'document' && '📄 Documento'}
                    {!['pc_code', 'document'].includes(searchResult.searchType) && searchResult.searchType}
                  </span>
                </div>
              )}
              
              {/* Tempo de execução (nova funcionalidade) */}
              {searchResult.executionTime && (
                <div>
                  Tempo: <span className="font-medium">{searchResult.executionTime}ms</span>
                  {searchResult.executionTime < 200 && <span className="text-green-600 ml-1">⚡ Rápido</span>}
                  {searchResult.executionTime >= 200 && searchResult.executionTime < 1000 && <span className="text-yellow-600 ml-1">⏱️ Normal</span>}
                  {searchResult.executionTime >= 1000 && <span className="text-red-600 ml-1">🐌 Lento</span>}
                </div>
              )}
              
              {/* Passos executados (funcionalidade existente) */}
              {searchResult.searchSteps && searchResult.searchSteps.length > 0 && (
                <div>
                  Passos: <span className="font-medium">{searchResult.searchSteps.join(' → ')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Parcelas testadas */}
        {searchResult.testedParcelas && searchResult.testedParcelas.length > 0 && (
          <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded">
            <div className="font-medium mb-2">Parcelas testadas:</div>
            <div className="space-y-1">
              {searchResult.testedParcelas.map((parcela, index) => (
                <div key={index} className={parcela === searchResult.foundParcela ? 'text-green-600 font-medium' : 'text-gray-500'}>
                  {parcela === searchResult.foundParcela ? '✅ ' : '❌ '}{parcela}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Erro */}
        {searchResult.error && (
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
            {searchResult.error}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SearchResult;
